<template>
  <div>
    <div v-if="error" class="mb-4">
      <server-error :error="error"/>
    </div>
    <div class="py-4 px-6 b-toolbar b-contract__toolbar">
      <div class="row b-toolbar__inner b-contract__toolbar-inner">
        <div class="col-6 b-toolbar__inner-sort">
          <div class="b-toolbar__inner-sort-variants">
            <button
              class="b-btn"
              :class="currentContractType === 'main' ? 'b-btn_type_active' : ''"
              @click="setCurrentContractType('main')"
            >{{ $t('CONTENT.contract.contractType_main') }}
            </button>
            <button
              class="b-btn"
              :class="currentContractType === 'alternative' ? 'b-btn_type_active' : ''"
              @click="setCurrentContractType('alternative')"
            >{{ $t('CONTENT.contract.contractType_alternative') }}
            </button>
          </div>
        </div>
        <div class="col-5 b-toolbar__inner-create">
          <div v-if="!isReadOnlyMode" class="mr-3 b-toolbar__item">
            <button
              @click="createContract"
              data-cy="create-contract"
              class="b-btn b-btn_type_primary"
              :disabled="isDisabledCreateContract"
            >
              <dynamic-icon
                class="b-btn__icon b-btn__icon_color_white"
                :class="!isDisabledCreateContract ? 'b-btn__icon_color_white' : '' "
                icon="plus"
              />
              {{ $t('CONTENT.contract.create_agreement') }}
            </button>
          </div>
          <div class="mr-3 b-toolbar__item b-import__choose">
            <ButtonWithDropdown
              :importData="exportData"
              :btnText="$t('CONTENT.export')"
              iconName="download"
              @onChooseValue="createDownloadLink($event)"
            ></ButtonWithDropdown>
          </div>
          <div v-if="!isReadOnlyMode" class="mr-3 b-toolbar__item b-import__choose">
            <ButtonWithDropdown
              :importData="importData"
              :btnText="$t('CONTENT.import')"
              iconName="xls"
              @onChooseValue="openImportDialog($event)"
            ></ButtonWithDropdown>
          </div>
          <div v-if="!isReadOnlyMode" class="mr-3 b-toolbar__item b-import__choose">
            <ButtonWithDropdown
              :importData="importData"
              :btnText="$t('CONTENT.download_template')"
              iconName="download"
              @onChooseValue="createDownloadLink($event)"
            ></ButtonWithDropdown>
          </div>
        </div>
      </div>
    </div>
    <ContractsFilter :currentContractType="currentContractType" :filterParams="filterData"
                     @resetFilter="onResetFilter"/>
    <div class="b-full-table b-contracts__table" v-if="currentContractType === 'main'">
      <div class="b-full-table__row b-full-table__row-header">
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.contract_number') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.external_id') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.contract_status') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.contract_date') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.lessee') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.lessor') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell b-table__cell_width_md">
          {{ $t('CONTENT.events.last_event') }}
        </div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.events.event_date') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.prolongation_type') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.initiator') }}</div>
      </div>
      <template v-for="currentContract in contracts">
        <ContractRow :currentContract="currentContract"/>
      </template>
    </div>
    <div class="b-full-table" v-if="currentContractType === 'alternative'">
      <div class="b-full-table__row b-full-table__row-header">
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.contract_number') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.contract_date') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.lessee') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.lessor') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.type_of_exception') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.prolongationType') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.contract.initiator') }}</div>
      </div>
      <template v-for="currentContract in contracts">
        <AlternativeContractRow :currentContract="currentContract"></AlternativeContractRow>
      </template>
    </div>
    <ContractPaging v-if="isShowPaging"
                    :filterParams="filterData"
                    :currentContractType="currentContractType"
                    :contractsCount="contractsCount"
                    :contractsWithErrorCount="contractsWithErrorCount"
                    :totalCount="totalCount">
    </ContractPaging>
    <popup name="contract-import-popup" height="auto" :scrollable="true">
      <ImportDialogWidget
        :importType="importType"
        v-if="isShowImportDialog && importType !== null"
        @close="closeImportDialog"
      ></ImportDialogWidget>
    </popup>
  </div>
</template>

<script>
import ContractRow from "./ContractTableRow";
import AlternativeContractRow from "../alternativeContracts/AlternativeContractTableRow";
import {mapGetters} from "vuex";
import ImportDialogWidget from "../../widgets/ImportDialogWidget";
import ButtonWithDropdown from "../../shared/ButtonWithDropdown";
import downloadFileMixin from "../../../mixins/downloadFile";
import EventRegistryAPI from "../../../plugins/eventRegistry/api/EventRegistryAPI";
import ImportTemplateAPI from "../../../api/ImportTemplateAPI";
import ContractAggregateAPI from "../../../api/ContractAggregateAPI";

export default {
  components: {
    ContractRow,
    ImportDialogWidget,
    ButtonWithDropdown,
    AlternativeContractRow
  },
  data() {
    return {
      error: null,
      isVisible: true,
      importType: null,
      isShowImportDialog: false,
      currentContractType: "main",
      contractsCount: null,
      contractsWithErrorCount: null,
      contracts: [],
      altContracts: [],
      totalCount: null,
      isShowPaging: true,
      isInitialized: false,
      isDisabledExportExcel: false,
      isReadOnlyMode: true,
      filterData: {
        startDate: null,
        endDate: null,
        havePaymentsFrom: null,
        havePaymentsTo: null,
        status: null,
        indexationMonthAndYear: null,
        contractSearchQuery: null,
        itemsPerPage: 10,
        page: 1,
      },
    };
  },
  mixins: [downloadFileMixin],
  computed: {
    ...mapGetters(["allContractAggregates"]),
    ...mapGetters(["allAlternativeContracts"]),
    ...mapGetters(["getContractSettings"]),
    isDisabledCreateContract() {
      return (
        this.getContractSettings.systemOwnerCounterpartyId === null ||
        this.getContractSettings.yearsToAddToPerpetuity === null ||
        this.getContractSettings.currencyCode === null ||
        this.getContractSettings.currencyCode === ""
      );
    },isDisabledExportExcel(){
      return this.isDisabledExportExcel;
    },
    exportData() {
      return [
        {
          text: this.$tc('CONTENT.upload_Initial_Data_Contracts', 1),
          value: "initialDataContracts"
        },
        {
          text: this.$t('CONTENT.contracts_registry'),
          value: "contractsRegistry"
        }
      ]},
    importData() {
      return [
        {
          text: this.$tc("CONTENT.contract.contract", 1),
          value: "contract"
        },
        {
          text: this.$t("CONTENT.contract.alternativeContract"),
          value: "alternativeContract"
        },
        {
          text: this.$t("CONTENT.events.events"),
          value: "event"
        },
        {
          text: this.$t("CONTENT.reportPage.lease_payments"),
          value: "payments"
        }
      ]
    },

  },
  async created() {
    this.setRoute();
    this.updateRoute();
    await this.loadContracts(true);
    this.isInitialized = true;
    this.role = localStorage.getItem('role');
    if (this.role === 'Admin' || this.role === 'Operator') {
      this.isReadOnlyMode = false;
    }
  },
  watch: {
    'filterData': {
      handler() {
        this.setRoute();
        this.loadContracts();
      },
      deep: true,
    },
    totalCount(newVal, oldVal) {
      if (newVal !== null && newVal !== oldVal) {
        this.isShowPaging = newVal >= this.filterData.itemsPerPage;
      }
    }
  },
  methods: {
    createContract() {
      this.$router.push({name: "Questions"});
    },
    openImportDialog(importType) {
      this.importType = importType;
      this.isShowImportDialog = true;
      this.$modal.show("contract-import-popup");
    },
    closeImportDialog() {
      this.isShowImportDialog = false;
      this.importType = null;
      this.$modal.hide("contract-import-popup");
    },
    createDownloadLink(importType) {
      this.importType = importType;
      this.downloadTemplate();
    },
    setCurrentContractType(contractType) {
      if (contractType === this.currentContractType) {
        return;
      }

      this.currentContractType = contractType;
      this.filterData.page = 1;
      this.setRoute();
      this.loadContracts();
    },
    async loadContracts(isInitCall) {
      if (!isInitCall && !this.isInitialized) {
        return;
      }
      this.error = null;
      this.contractsCount = null;
      this.contractsWithErrorCount = null;
      this.contracts = [];
      this.totalCount = null;
      this.altContracts = [];

      let response = null;

      if (this.currentContractType === 'main') {
        this.totalCount = await this.$store.dispatch("getCount", this.filterData);
        response = await this.$store.dispatch("getAllContractAggregates", this.filterData);
        this.contracts = response.contracts;
        this.contractsCount = response.contracts.length;
        this.contractsWithErrorCount = response.errors.exceptions.length;

        if (response.errors.exceptions.length > 0 && this.error === null) {
          // this will format error to server error format
          this.error = {
            response: {data: {Exceptions: response.errors.exceptions}}
          };
        }
      } else if (this.currentContractType === "alternative") {
        this.totalCount = await this.$store.dispatch("getAlternativeCount", this.filterData);
        response = await this.$store.dispatch("getAllAlternativeContracts", this.filterData);
        this.contracts = response;
        this.contractsCount = response.length;
      }

      if (this.contracts.length === 0) {
        this.isShowPaging = false;
      }
    },
    setRoute() {
      let routeParameters = {
        contractType: this.currentContractType
      };
      for (let prop in this.filterData) {
        if (!this.isInitialized) {
          return;
        }
        let newParameters = {};
        newParameters[prop] = this.filterData[prop];
        if (this.filterData[prop] !== null) {
          Object.assign(routeParameters, newParameters);
        }
      }
      this.$router.replace ({query: routeParameters});
    },
    updateRoute() {
      let queryParams = this.$route.query;
      for (const queryParamsKey in queryParams) {
        if (this.$route.query.page != null) {
          this.filterData.page = +this.$route.query.page;
        }
        if (this.$route.query.itemsPerPage != null) {
          this.filterData.page = +this.$route.query.page;
        }
        if (this.$route.query.endDate != null) {
          this.filterData.endDate = this.$route.query.endDate;
        }
        if (this.$route.query.startDate != null) {
          this.filterData.startDate = this.$route.query.startDate;
        }
        if (this.$route.query.havePaymentsTo != null) {
          this.filterData.havePaymentsTo = this.$route.query.havePaymentsTo;
        }
        if (this.$route.query.havePaymentsFrom != null) {
          this.filterData.havePaymentsFrom = this.$route.query.havePaymentsFrom;
        }
        if (this.$route.query.searchQuery != null) {
          this.filterData.searchQuery = this.$route.query.searchQuery;
        }
        if (this.$route.query.lessorId != null) {
          this.filterData.lessorId = parseInt(this.$route.query.lessorId);
        }
        if (this.$route.query.lesseeId != null) {
          this.filterData.lesseeId = parseInt(this.$route.query.lesseeId);
        }
        if (this.$route.query.indexationMonthAndYear != null) {
          this.filterData.indexationMonthAndYear = this.$route.query.indexationMonthAndYear;
        }
        if (this.$route.query.status != null) {
          this.filterData.status = this.$route.query.status;
        }
        if (this.$route.query.contractType != null) {
          this.currentContractType = this.$route.query.contractType;
        }
      }
    },
    onResetFilter() {
      this.filterData = {
        startDate: null,
        endDate: null,
        havePaymentsFrom: null,
        havePaymentsTo: null,
        indexationMonthAndYear: null,
        contractSearchQuery: null,
        lessorSearchQuery: null,
        lesseeSearchQuery: null,
        itemsPerPage: 10,
        page: 1,
      }
    },
  }
};
</script>
